import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Button,
  Chip,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { ScrapEyesEventWithReviewData } from "../contexts/ManualReviewEventContext";
import { formatReviewReasons } from "../utils/reviewReasons";
import { EventFlowDisplay } from "./EventFlowDisplay";

interface EditEventHeaderProps {
  mergedEvent: ScrapEyesEventWithReviewData;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onToggleResolved: () => void;
  hasPendingChanges: boolean;
  onDiscard: () => void;
  readOnly?: boolean;
}

export const EditEventHeader = ({
  mergedEvent,
  onNavigatePrevious,
  onNavigateNext,
  onToggleResolved,
  hasPendingChanges,
  onDiscard,
  readOnly = false,
}: EditEventHeaderProps) => {
  const { t } = useTenantTranslation();
  const isResolved = mergedEvent.resolved ?? false;

  return (
    <DialogTitle sx={{ pb: 0 }}>
      <Stack
        spacing={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{t("resolveConflict")}</Typography>
            <Chip
              label={
                isResolved
                  ? t("conflictStatusResolved")
                  : t("conflictStatusNotResolved")
              }
              color={isResolved ? "success" : "warning"}
              size="small"
            />
          </Stack>
          <EventFlowDisplay event={mergedEvent} />
          <Typography variant="caption" color="text.secondary">
            {formatReviewReasons(mergedEvent.review_reasons)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {hasPendingChanges && (
            <>
              <Chip label={t("changesMade")} color="info" size="small" />
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={onDiscard}
              >
                {t("discardChanges")}
              </Button>
            </>
          )}

          <Tooltip title={t("previousConflict")}>
            <IconButton
              onClick={onNavigatePrevious}
              disabled={!onNavigatePrevious}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("nextConflict")}>
            <IconButton
              onClick={onNavigateNext}
              disabled={!onNavigateNext}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant={isResolved ? "outlined" : "contained"}
            onClick={onToggleResolved}
            disabled={readOnly}
            size="small"
          >
            {isResolved ? t("markAsNotResolved") : t("markAsResolved")}
          </Button>
        </Stack>
      </Stack>
    </DialogTitle>
  );
};
